import { useHighlyConfidentialValue } from "@/features/highly-confidential";
import { memo, useCallback } from "react";
import { useFormContext } from "../../../context/FormContext";
import type { FieldRenderInputProps } from "../../../types";
import type { BaseFieldProps } from "../BaseField";
import { BaseField } from "../BaseField";
import { HighlyConfidentialTextFieldEditablePresenter } from "./HighlyConfidentialTextField.editable.presenter";
import { HighlyConfidentialTextFieldReadOnlyPresenter } from "./HighlyConfidentialTextField.readonly.presenter";

interface HighlyConfidentialTextFieldContainerProps extends BaseFieldProps {
  /** プレースホルダー */
  placeholder?: string;
}

/**
 * 高機密テキストフィールドのコンテナー
 */
export const HighlyConfidentialTextFieldContainer = memo(
  ({
    fieldId,
    field,
    layout,
    readOnly,
    disabled,
    className,
    placeholder,
    "data-testid": testId,
  }: HighlyConfidentialTextFieldContainerProps) => {
    const formContext = useFormContext();

    // 秘匿値の状態管理
    const { isRevealed, secretValue, isLoading, toggleReveal } =
      useHighlyConfidentialValue({
        operationTaskInstanceId: formContext?.operationTaskInstanceId || "",
        fieldId,
      });

    // 入力レンダリング関数
    const renderInput = useCallback(
      (props: FieldRenderInputProps) => {
        // 入力値を文字列に変換
        const stringValue =
          props.value !== undefined && props.value !== null
            ? String(props.value)
            : "";

        // ReadOnlyモードの場合
        if (props.readOnly || readOnly) {
          // 値があるかどうかを判定
          const hasValue =
            (stringValue && stringValue.trim() !== "") || !!secretValue;

          // ReadOnlyモードでは、フォーム送信時の楽観的更新により、実際の値がフォームストアに残るため、props.value（フォームストアの値）を信用しない
          //
          // 表示ロジック
          // - isRevealedがtrueの時だけsecretValue（APIから取得した実際の値）を表示
          // - 値がある場合はマスク "***" を表示
          // - 未入力の場合は空文字
          let displayValue = "";
          if (isRevealed && secretValue) {
            displayValue = secretValue; // 復号化された実際の値
          } else if (hasValue) {
            displayValue = "***"; // マスク表示
          }

          return (
            <HighlyConfidentialTextFieldReadOnlyPresenter
              displayValue={displayValue}
              isRevealed={isRevealed}
              isLoading={isLoading}
              disabled={props.disabled || disabled}
              testId={testId}
              onToggleReveal={toggleReveal}
            />
          );
        }

        // 編集モードの場合
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          props.onChange(e.target.value);
        };

        return (
          <HighlyConfidentialTextFieldEditablePresenter
            id={props.id}
            name={props.name}
            value={stringValue}
            placeholder={props.placeholder || placeholder}
            disabled={props.disabled}
            readOnly={props.readOnly}
            required={props.required}
            error={!!props.error}
            ariaInvalid={!!props.error}
            ariaDescribedBy={props.error ? `${props.id}-error` : undefined}
            testId={props["data-testid"]}
            onChange={handleChange}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
          />
        );
      },
      [
        readOnly,
        disabled,
        placeholder,
        testId,
        isRevealed,
        secretValue,
        isLoading,
        toggleReveal,
      ],
    );

    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        readOnly={readOnly}
        disabled={disabled}
        layout={layout}
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

HighlyConfidentialTextFieldContainer.displayName =
  "HighlyConfidentialTextFieldContainer";
